import {
  useCallback,
} from 'react';

import {
  fetchWithRetry,
} from '@/lib/apiRequest';

import {
  parsePublicationInboxReviewCandidate,
  type PublicationInboxReviewCandidate,
  type PublicationInboxReviewDecision,
} from '@/lib/publicationInboxDecision';

type ApiErrorBody = {
  success?: boolean;
  error?: string;
};

export type PublicationInboxReviewContext = {
  analysisRunId: string;
  candidate:
    PublicationInboxReviewCandidate;
  latestReviewId: string | null;
  latestReviewDecision:
    string | null;
  latestReviewedAnalysis:
    PublicationInboxReviewCandidate | null;
};

export type PublicationInboxReviewMutationResult = {
  reviewId: string;
  operationalizationState:
    string | null;
  operationalizationAllowed:
    boolean;
};

const errorText = (
  value: unknown,
  fallback: string
) => {
  const text = String(
    value ?? ''
  ).trim();

  const known: Record<
    string,
    string
  > = {
    ai_review_correction_unchanged:
      'Nenhuma correção foi feita. Altere ao menos um campo ou aceite a análise da IA.',
    ai_review_supersedes_required:
      'A análise já possui revisão. Recarregue a publicação antes de decidir novamente.',
    ai_review_must_supersede_latest:
      'Existe revisão humana mais recente. Recarregue a publicação antes de continuar.',
    ai_review_correction_invalid:
      'A correção não atende ao contrato estruturado. Confira os campos informados.',
    ai_review_run_without_analysis:
      'A execução selecionada não possui análise estruturada utilizável.',
  };

  return (
    known[text] ??
    (text || fallback)
  );
};

export function usePublicationInboxActions() {
  const loadReviewContext =
    useCallback(
      async (
        publicationId: string
      ): Promise<PublicationInboxReviewContext> => {
        const response =
          await fetchWithRetry(
            `/api/publications/${encodeURIComponent(
              publicationId
            )}/ai-analysis`,
            {},
            {
              timeoutMs: 15000,
              retries: 1,
              retryDelayMs: 300,
            }
          );

        const data =
          (await response.json()) as
            ApiErrorBody & {
              currentAnalysisRun?: {
                id?: string;
                analysis?: unknown;
              } | null;
              latestHumanReview?: {
                id?: string;
                decision?: string;
                reviewedAnalysis?: unknown;
              } | null;
            };

        if (
          !response.ok ||
          !data.success
        ) {
          throw new Error(
            errorText(
              data.error,
              'Não foi possível carregar a análise para revisão.'
            )
          );
        }

        const analysisRunId =
          String(
            data.currentAnalysisRun
              ?.id ?? ''
          ).trim();

        if (
          !analysisRunId ||
          !data.currentAnalysisRun
            ?.analysis
        ) {
          throw new Error(
            'Não existe análise estruturada utilizável para esta publicação.'
          );
        }

        const candidate =
          parsePublicationInboxReviewCandidate(
            data.currentAnalysisRun
              .analysis
          );

        let latestReviewedAnalysis:
          PublicationInboxReviewCandidate | null =
          null;

        if (
          data.latestHumanReview
            ?.reviewedAnalysis
        ) {
          latestReviewedAnalysis =
            parsePublicationInboxReviewCandidate(
              data.latestHumanReview
                .reviewedAnalysis
            );
        }

        return {
          analysisRunId,
          candidate,
          latestReviewId:
            String(
              data.latestHumanReview
                ?.id ?? ''
            ).trim() || null,
          latestReviewDecision:
            String(
              data.latestHumanReview
                ?.decision ?? ''
            ).trim() || null,
          latestReviewedAnalysis,
        };
      },
      []
    );

  const submitReview =
    useCallback(
      async (
        input: {
          publicationId: string;
          analysisRunId: string;
          decision:
            PublicationInboxReviewDecision;
          correctedAnalysis?:
            PublicationInboxReviewCandidate;
          notes?: string | null;
          supersedesReviewId?:
            string | null;
        }
      ): Promise<PublicationInboxReviewMutationResult> => {
        const payload: {
          analysisRunId: string;
          decision:
            PublicationInboxReviewDecision;
          correctedAnalysis?:
            PublicationInboxReviewCandidate;
          notes: string | null;
          supersedesReviewId:
            string | null;
        } = {
          analysisRunId:
            input.analysisRunId,
          decision:
            input.decision,
          notes:
            String(
              input.notes ?? ''
            ).trim() || null,
          supersedesReviewId:
            input.supersedesReviewId ??
            null,
        };

        if (
          input.decision ===
          'corrected'
        ) {
          if (
            !input.correctedAnalysis
          ) {
            throw new Error(
              'A análise corrigida é obrigatória.'
            );
          }

          payload.correctedAnalysis =
            input.correctedAnalysis;
        }

        const response =
          await fetchWithRetry(
            `/api/publications/${encodeURIComponent(
              input.publicationId
            )}/ai-reviews`,
            {
              method: 'POST',
              headers: {
                'Content-Type':
                  'application/json',
              },
              body:
                JSON.stringify(
                  payload
                ),
            },
            {
              timeoutMs: 15000,
              retries: 0,
            }
          );

        const data =
          (await response.json()) as
            ApiErrorBody & {
              review?: {
                id?: string;
              };
              operationalization?: {
                state?: string;
                error?: string;
                operationalization?: {
                  state?: string;
                };
              } | null;
              operationalizationAllowed?:
                boolean;
            };

        if (
          !response.ok ||
          !data.success
        ) {
          throw new Error(
            errorText(
              data.error,
              'Não foi possível registrar a revisão humana.'
            )
          );
        }

        const reviewId =
          String(
            data.review?.id ?? ''
          ).trim();

        if (!reviewId) {
          throw new Error(
            'A revisão foi recebida, mas o identificador persistido não retornou.'
          );
        }

        const operationalizationState =
          String(
            data.operationalization
              ?.operationalization
              ?.state ??
              data.operationalization
                ?.state ??
              ''
          ).trim() || null;

        return {
          reviewId,
          operationalizationState,
          operationalizationAllowed:
            Boolean(
              data.operationalizationAllowed
            ),
        };
      },
      []
    );

  const confirmDeadline =
    useCallback(
      async (
        deadlineId: string,
        dueDate: string
      ) => {
        const normalizedDate =
          String(
            dueDate ?? ''
          ).trim();

        if (
          !/^\d{4}-\d{2}-\d{2}$/.test(
            normalizedDate
          )
        ) {
          throw new Error(
            'Informe a data final no formato válido.'
          );
        }

        const response =
          await fetchWithRetry(
            `/api/case-deadlines/${encodeURIComponent(
              deadlineId
            )}`,
            {
              method: 'PUT',
              headers: {
                'Content-Type':
                  'application/json',
              },
              body:
                JSON.stringify({
                  dueDate:
                    normalizedDate,
                }),
            },
            {
              timeoutMs: 15000,
              retries: 0,
            }
          );

        const data =
          (await response.json()) as
            ApiErrorBody & {
              caseDeadline?: {
                id?: string;
                dueDate?: string | null;
                taskId?: string | null;
              } | null;
            };

        if (
          !response.ok ||
          !data.success
        ) {
          throw new Error(
            errorText(
              data.error,
              'Não foi possível confirmar a data final.'
            )
          );
        }

        return data.caseDeadline ??
          null;
      },
      []
    );

  return {
    loadReviewContext,
    submitReview,
    confirmDeadline,
  };
}